/**
 * The digit bar — the naming question, asked ON the character.
 *
 * ALLEN'S DESIGN, 2026-08-12: *"we should do the digit names in the 3d space —
 * like a banner with n buttons flying off the current disc. So user clicks
 * disc, the long bar with thumb · index · mid flies out right there. Minimal
 * mouse movement."* The first surface put the answer in the right-hand panel,
 * which meant every digit cost a round trip across the screen — his measured
 * complaint was the eye jump, not the click count.
 *
 * It is also the brief's own law rather than a preference. XD §1.1: *"in this
 * register, evidence lives ON THE CHARACTER … the denominator is the body."*
 * The question "which digit is this?" is a question about a place on a model,
 * and the model is where it belongs.
 *
 * WHY DOM AND NOT 3D GEOMETRY. Text in a WebGPU scene needs a font atlas, and
 * it would land blurry at exactly the sizes this is read at. A DOM element
 * anchored to the marker's projected position is crisp, keyboard-reachable and
 * accessible for free (XD §4: *"every control ships with keyboard path +
 * role/label at birth"*), and it follows the disc every frame, so it reads as
 * attached to the hand rather than floating over it.
 *
 * THE ± IS THE DIGIT COUNT, and it is anatomy, not arithmetic. `nameFingerSet`
 * is the single definition of which digits a hand of N has — the ratified rule
 * that a four-digit creature is missing its RING, not its pinky (Allen, on the
 * kobold, 2026-07-15). Pressing − drops the innermost finger from the offer;
 * pressing + puts it back. Allen's frog: two named, the remaining options thin
 * out, a fourth digit turns out to be there, + restores Index and the naming
 * continues.
 *
 * A pure view: it renders what it is handed and reports clicks. Which names are
 * free, what the count means, and what happens when the last one is used are
 * all the panel's business.
 * Depends on: kernel (`nameFingerSet` — the anatomy), nothing else.
 * Depended on by: hands-panel.
 */
import { nameFingerSet, type DigitName } from '@harryhausen/kernel';

export interface DigitFlyoutDeps {
  /** Where the anchor marker is on screen right now, or null if it is not drawn. */
  screenXY(): { readonly x: number; readonly y: number } | null;
  /** A name was chosen — a trusted click, always. */
  onPick(digit: DigitName): void;
  /**
   * THE WRIST (ADR-025): "this place is the wrist." Offered FIRST on a hand
   * that has no wrist answer yet (Allen's order: wrist → tips → knuckles) —
   * the click's station moves the hand joint along the arm.
   */
  onPickWrist(): void;
  /**
   * THE SECOND ROW (ADR-024 §2.2): "this place is <digit>'s first knuckle."
   * Offered only for digits that already have a named tip pin — a knuckle
   * belongs to a digit somebody vouched for.
   */
  onPickKnuckle(digit: DigitName): void;
  /** The digit COUNT changed: ±1, clamped by the flyout to 1–5. */
  onCount(next: number): void;
  /** Throw this candidate away (the old "not a digit"), or un-name a placed pin. */
  onReject(): void;
  /** Dismissed without answering — Escape, or a click elsewhere. */
  onDismiss(): void;
}

export interface DigitFlyoutState {
  /** How many digits this hand is believed to have — the ± value. */
  readonly expected: number;
  /** Names already worn by other pins; offered but marked, never silently gone. */
  readonly taken: readonly DigitName[];
  /** The name this marker already carries, if it is a placed pin being re-opened. */
  readonly current: DigitName | null;
  /**
   * Digits eligible for a knuckle — the ones with a NAMED tip pin. Empty hides
   * the row entirely (nothing to attach a knuckle to yet).
   */
  readonly knuckleTargets?: readonly DigitName[];
  /** Digits already wearing a knuckle — marked, and a pick MOVES theirs here. */
  readonly knuckleTaken?: readonly DigitName[];
  /**
   * When the opened marker IS an existing knuckle: whose it is. The bar then
   * shows only the knuckle row (re-aim it at another digit, or × removes it) —
   * a knuckle marker asks a knuckle question.
   */
  readonly knuckleCurrent?: DigitName | null;
  /**
   * Offer the WRIST on this bar (ADR-025): a bare-mesh click on a hand with no
   * wrist answer yet. Leads the bar — it is the first question on this hand.
   */
  readonly wristOffered?: boolean;
  /** The opened marker IS the wrist: the bar shows only × (take it away — the joint stands). */
  readonly wristCurrent?: boolean;
  /**
   * The click landed OFF the hand region (forearm-weighted flesh — where a
   * wrist crease is): offer the wrist and nothing else, plus × to dismiss.
   */
  readonly wristOnly?: boolean;
}

export interface DigitFlyout {
  open(state: DigitFlyoutState): void;
  update(state: DigitFlyoutState): void;
  close(): void;
  readonly isOpen: boolean;
  dispose(): void;
}

/** The five, in anatomical order — the vocabulary, never a subset of it. */
export const ALL_DIGITS: readonly DigitName[] = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky'];

/**
 * Which names a hand of `expected` digits offers. THE THUMB IS ALWAYS ONE OF
 * THEM (the schema's own reading of `hand-N` — `hand-4` is a thumb and three
 * fingers), and the fingers come from `nameFingerSet`, which is where the
 * ulnar-most-is-Pinky ruling lives. One definition of that anatomy, imported.
 */
export function offeredDigits(expected: number): readonly DigitName[] {
  const clamped = Math.max(1, Math.min(ALL_DIGITS.length, Math.round(expected)));
  return clamped <= 1 ? ['Thumb'] : ['Thumb', ...nameFingerSet(clamped - 1)];
}

export function createDigitFlyout(host: HTMLElement, deps: DigitFlyoutDeps): DigitFlyout {
  const root = document.createElement('div');
  root.className = 'digit-flyout';
  root.setAttribute('role', 'radiogroup');
  root.setAttribute('aria-label', 'name this digit');
  root.hidden = true;
  host.append(root);

  let open = false;
  let frame = 0;
  let state: DigitFlyoutState = { expected: 5, taken: [], current: null };

  /** Follow the disc. The bar is attached to a place on a moving character. */
  function reposition(): void {
    const at = deps.screenXY();
    if (at === null) {
      // The marker stopped being drawn (a redraw, a subject swap) — the bar has
      // nothing to be attached to, and a bar attached to nothing is a lie about
      // which digit is being named.
      close();
      return;
    }
    const rect = root.getBoundingClientRect();
    // Above the disc and centred on it, clamped into the window so a hand near
    // an edge does not push the answer off-screen.
    const x = Math.min(Math.max(at.x - rect.width / 2, 8), window.innerWidth - rect.width - 8);
    const y = Math.min(Math.max(at.y - rect.height - 18, 8), window.innerHeight - rect.height - 8);
    root.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
    if (open) frame = requestAnimationFrame(reposition);
  }

  function render(): void {
    root.replaceChildren();

    // ── WRIST-EDIT MODE (ADR-025): the marker IS the wrist ──────────────────
    // One question: keep it, or take it away (the joint stands, D4). Moving it
    // is its own gesture — click the new place on the model and pick wrist.
    if (state.wristCurrent === true) {
      const label = document.createElement('span');
      label.className = 'flyout-wrist-label';
      label.textContent = 'wrist';
      root.append(label);
      const reject = document.createElement('button');
      reject.type = 'button';
      reject.className = 'flyout-reject';
      reject.textContent = '×';
      reject.title = 'Remove the wrist answer — the hand joint stays where the rig put it.';
      reject.setAttribute('aria-label', 'remove wrist');
      reject.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onReject();
      });
      root.append(reject);
      return;
    }

    // ── KNUCKLE-EDIT MODE (ADR-024 §2.2): the marker IS a knuckle ──────────
    // A knuckle marker asks a knuckle question: whose is it (re-aim), or take
    // it away (×). The tip row would be noise here — a knuckle cannot become
    // a tip by renaming.
    if (state.knuckleCurrent !== undefined && state.knuckleCurrent !== null) {
      renderKnuckleRow(root);
      const reject = document.createElement('button');
      reject.type = 'button';
      reject.className = 'flyout-reject';
      reject.textContent = '×';
      reject.title = 'Remove this knuckle — the digit goes back to routing from the wrist.';
      reject.setAttribute('aria-label', 'remove knuckle');
      reject.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onReject();
      });
      root.append(reject);
      return;
    }

    // ── THE WRIST, FIRST (ADR-025 D3): on a hand with no wrist answer the bar
    // leads with it, because every other answer on this hand is measured from
    // it. Once answered it is a small mark on the model and the tip row leads.
    if (state.wristOnly === true) {
      const wrist = document.createElement('button');
      wrist.type = 'button';
      wrist.className = 'flyout-wrist';
      wrist.dataset['digit'] = 'Wrist';
      wrist.textContent = '⌖ this is the wrist';
      wrist.title = 'This click is on flesh the rig weights to the forearm — which is where a wrist crease is. Say it is the wrist and the hand joint moves along the arm to here.';
      wrist.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onPickWrist();
      });
      root.append(wrist);
      const reject = document.createElement('button');
      reject.type = 'button';
      reject.className = 'flyout-reject';
      reject.textContent = '×';
      reject.title = 'Not the wrist — dismiss this click.';
      reject.setAttribute('aria-label', 'dismiss');
      reject.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onReject();
      });
      root.append(reject);
      return;
    }

    if (state.wristOffered === true) {
      const wrist = document.createElement('button');
      wrist.type = 'button';
      wrist.className = 'flyout-wrist';
      wrist.dataset['digit'] = 'Wrist';
      wrist.textContent = '⌖ wrist';
      wrist.title =
        'This place is the WRIST. Tripo sometimes drops the hand joint into the palm; ' +
        'this moves it along the arm to here (the lateral seat stays measured) and re-owns the palm root.';
      wrist.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onPickWrist();
      });
      root.append(wrist);
    }

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'flyout-count';
    minus.textContent = '−';
    minus.title = 'This hand has one digit fewer — drops the innermost finger from the list (a four-digit hand is missing its RING).';
    minus.setAttribute('aria-label', 'one digit fewer');
    minus.disabled = state.expected <= 1;
    minus.addEventListener('click', (event) => {
      if (!event.isTrusted) return;
      deps.onCount(state.expected - 1);
    });
    root.append(minus);

    for (const digit of offeredDigits(state.expected)) {
      const taken = state.taken.includes(digit) && digit !== state.current;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = digit === state.current ? 'flyout-digit flyout-digit-current' : 'flyout-digit';
      button.dataset['digit'] = digit;
      button.textContent = digit;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(digit === state.current));
      if (taken) {
        // Worn by another pin: shown struck through rather than removed, so the
        // bar's shape stays stable and a person can see WHY a name is missing.
        button.classList.add('flyout-digit-taken');
        button.disabled = true;
        button.title = 'Already on another pin — remove that one first.';
      }
      button.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onPick(digit);
      });
      root.append(button);
    }

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'flyout-count';
    plus.textContent = '+';
    plus.title = 'This hand has one digit more — puts the next finger back on the list.';
    plus.setAttribute('aria-label', 'one digit more');
    plus.disabled = state.expected >= ALL_DIGITS.length;
    plus.addEventListener('click', (event) => {
      if (!event.isTrusted) return;
      deps.onCount(state.expected + 1);
    });
    root.append(plus);

    const reject = document.createElement('button');
    reject.type = 'button';
    reject.className = 'flyout-reject';
    reject.textContent = '×';
    reject.title = state.current === null ? 'Not a digit — hide this candidate. You can put it back.' : 'Remove this pin.';
    reject.setAttribute('aria-label', 'not a digit');
    reject.addEventListener('click', (event) => {
      if (!event.isTrusted) return;
      deps.onReject();
    });
    root.append(reject);

    // ── THE SECOND ROW (ADR-024 §2.2): "…or this is a digit's KNUCKLE" ─────
    // Offered under the names, on the same bar, only once at least one digit
    // has a named tip to attach a knuckle to. Same discs, same hues — a second
    // row on the bar rather than a new instrument (ADR-023 §12.5).
    if ((state.knuckleTargets?.length ?? 0) > 0) renderKnuckleRow(root);
  }

  /** The knuckle row: one button per eligible digit, in its own hue. */
  function renderKnuckleRow(host: HTMLElement): void {
    const row = document.createElement('div');
    row.className = 'flyout-knuckle-row';
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'name this knuckle');
    const label = document.createElement('span');
    label.className = 'flyout-knuckle-label';
    label.textContent = 'knuckle';
    row.append(label);
    for (const digit of state.knuckleTargets ?? []) {
      const worn = state.knuckleTaken?.includes(digit) ?? false;
      const current = state.knuckleCurrent === digit;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = current ? 'flyout-knuckle flyout-digit-current' : 'flyout-knuckle';
      button.dataset['digit'] = digit;
      // The ring glyph is the knuckle's mark on the model too — one symbol.
      button.textContent = `◦ ${digit}`;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(current));
      button.title = `This place is ${digit}'s first knuckle.`;
      if (worn && !current) {
        // SAME DISAPPEARING ACT AS THE TIP ROW (Allen, field 2026-08-14): an
        // answered knuckle strikes through and disables, exactly like a taken
        // digit name — a silent move is an answer changed by machinery. Moving
        // one is its own gesture: click its dot on the model.
        button.classList.add('flyout-digit-taken');
        button.disabled = true;
        button.title = `${digit}'s knuckle is already placed — click its dot on the model to move or remove it.`;
      }
      button.addEventListener('click', (event) => {
        if (!event.isTrusted) return;
        deps.onPickKnuckle(digit);
      });
      row.append(button);
    }
    host.append(row);
  }

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!open || event.key !== 'Escape') return;
    deps.onDismiss();
  };
  window.addEventListener('keydown', onKeyDown);

  function close(): void {
    if (!open) return;
    open = false;
    cancelAnimationFrame(frame);
    root.hidden = true;
    root.replaceChildren();
  }

  return {
    get isOpen() {
      return open;
    },
    open(next) {
      state = next;
      render();
      root.hidden = false;
      open = true;
      cancelAnimationFrame(frame);
      reposition();
    },
    update(next) {
      state = next;
      if (!open) return;
      render();
    },
    close,
    dispose() {
      close();
      window.removeEventListener('keydown', onKeyDown);
      root.remove();
    },
  };
}
